
import { auth } from '../firebase/config';

/**
 * Get the current user's authentication token
 * @param {number} maxRetries - Maximum number of retries for token retrieval
 * @param {number} retryDelay - Delay between retries in milliseconds
 * @returns {Promise<string>} The user's ID token
 * @throws {Error} If user is not authenticated
 */
export const getAuthToken = async (maxRetries = 3, retryDelay = 1000) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const user = auth.currentUser;
      if (!user) {
        if (attempt < maxRetries) {
          console.log(`Auth token attempt ${attempt}: No user found, retrying in ${retryDelay}ms...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          continue;
        }
        throw new Error('No authenticated user found');
      }
      
      const token = await user.getIdToken();
      if (!token) {
        if (attempt < maxRetries) {
          console.log(`Auth token attempt ${attempt}: Failed to get token, retrying in ${retryDelay}ms...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          continue;
        }
        throw new Error('Failed to get authentication token');
      }
      
      return token;
    } catch (error) {
      if (attempt === maxRetries) {
        console.error('Error getting auth token after all retries:', error);
        throw new Error('Authentication failed: ' + error.message);
      }
      console.log(`Auth token attempt ${attempt} failed:`, error.message);
      await new Promise(resolve => setTimeout(resolve, retryDelay));
    }
  }
};

/**
 * Check if the current user is authenticated
 * @returns {boolean} True if user is authenticated, false otherwise
 */
export const isAuthenticated = () => {
  const user = auth.currentUser;
  return !!user;
};

/**
 * Get the current user's ID
 * @returns {string|null} The user's ID or null if not authenticated
 */
export const getCurrentUserId = () => {
  const user = auth.currentUser;
  return user?.uid || null;
};

/**
 * Get the current user's email
 * @returns {string|null} The user's email or null if not authenticated
 */
export const getCurrentUserEmail = () => {
  const user = auth.currentUser;
  return user?.email || null;
};

/**
 * Wait for authentication to be ready
 * @param {number} timeout - Maximum time to wait in milliseconds
 * @returns {Promise<boolean>} True if auth is ready, false if timeout
 */
export const waitForAuthReady = async (timeout = 10000) => {
  const startTime = Date.now();
  
  while (Date.now() - startTime < timeout) {
    if (auth.currentUser) {
      return true;
    }
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  return false;
};

/**
 * Check if authentication is ready and user is authenticated
 * @returns {boolean} True if auth is ready and user is authenticated
 */
export const isAuthReady = () => {
  return !!auth.currentUser;
};

/**
 * Get authentication status for debugging
 * @returns {Object} Authentication status information
 */
export const getAuthStatus = () => {
  const user = auth.currentUser;
  return {
    isAuthenticated: !!user,
    userId: user?.uid || null,
    email: user?.email || null,
    emailVerified: user?.emailVerified || false,
    lastSignInTime: user?.metadata?.lastSignInTime || null,
    creationTime: user?.metadata?.creationTime || null
  };
};
